<script setup>
import ButtonSubmit from "./ButtonSubmit.vue";
import Footer from "./FooterComponent.vue";
import CryptoJS from 'crypto-js'
import api from "../api/api";
import Swal from "sweetalert2";
import store from "../store/store.js";
import { flashMessage } from "../functions.js";
</script>

<template>
  <div class="login-page">
    <div class="login-main">
      <section class="login-hero">
        <div class="login-hero-top">
          <h1 class="login-hero-title">{{ brandData && brandData.login_hero_text ? brandData.login_hero_text : "Grow Your Leadership Skills" }}</h1>
          <div class="login-hero-tag">
            <span class="login-hero-tag-line"></span>
            <span class="login-hero-tag-text">Curated Excellence</span>
          </div>
        </div>
        <div class="login-hero-ticks" aria-hidden="true">
          <span class="tick tick-1"></span>
          <span class="tick tick-2"></span>
          <span class="tick tick-3"></span>
        </div>
        <div class="login-hero-graphic" aria-hidden="true">
          <div class="stair stair-1"></div>
          <div class="stair stair-2"></div>
          <div class="stair stair-3"></div>
          <div class="stair stair-4"></div>
          <div class="stair stair-5"></div>
        </div>
      </section>

      <section class="login-panel">
        <div class="login-panel-inner">
          <div class="login-heading">
            <img v-if="logoPath" :src="logoPath" alt="Brand Logo" class="login-panel-logo" />
            <h3>Set Your New Password</h3>
            <p class="login-subtext">
              Enter your new preferred password here for secure access to your account.
            </p>
          </div>

          <div class="form-group">
            <label for="newPasswordInput" class="field-label">New Password</label>
            <div class="password-input-wrap">
              <input
                :type="showPasswordField ? 'text' : 'password'"
                v-model="inputPassword.password"
                class="form-control"
                placeholder="Enter your new password"
                required
                id="newPasswordInput"
              />
              <span class="eye-toggle" @click="showPasswordField = !showPasswordField">
                <i :class="['fa', 'eye-icon-color', showPasswordField ? 'fa-eye-slash' : 'fa-eye']" aria-hidden="true"></i>
              </span>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPasswordInput" class="field-label">Confirm New Password</label>
            <div class="password-input-wrap">
              <input
                :type="showConfirmPasswordField ? 'text' : 'password'"
                v-model="inputPassword.confirmPassword"
                class="form-control"
                placeholder="Re-enter your new password"
                required
                id="confirmPasswordInput"
              />
              <span class="eye-toggle" @click="showConfirmPasswordField = !showConfirmPasswordField">
                <i :class="['fa', 'eye-icon-color', showConfirmPasswordField ? 'fa-eye-slash' : 'fa-eye']" aria-hidden="true"></i>
              </span>
            </div>
          </div>

          <div v-if="inputPassword.password" class="strength-card">
            <div class="strength-label">
              <i class="fas fa-shield-alt strength-icon" aria-hidden="true"></i>
              Password Strength
            </div>
            <div class="strength-bar-track">
              <div
                class="strength-bar-fill"
                :class="`strength-${passwordStrength.color}`"
                :style="{ width: (passwordStrength.score * 25) + '%' }"
              ></div>
            </div>
            <div class="strength-text" :class="`strength-text-${passwordStrength.color}`">
              {{ passwordStrength.text }}
            </div>
            <ul class="requirements-list">
              <li v-for="req in passwordRequirements" :key="req.text" :class="req.met ? 'req-met' : 'req-unmet'">
                <i :class="['fas', req.met ? 'fa-check' : 'fa-times']" aria-hidden="true"></i>
                {{ req.text }}
              </li>
            </ul>
          </div>

          <p v-if="selectedLanguages.length > 1 && !(selectedLanguages.length === 1 && selectedLanguages[0] === 'en')" class="login-subtext language-intro">
            Choose your preferred language for the system interface.
          </p>

          <div v-if="selectedLanguages.length > 1 && !(selectedLanguages.length === 1 && selectedLanguages[0] === 'en')" class="form-group">
            <label for="languageSelect" class="field-label">Select Language</label>
            <select id="languageSelect" v-model="selectedLanguage" class="form-control language-select" required>
              <option v-for="lang in filteredAvailableLanguages" :key="lang.code" :value="lang.code">
                {{ lang.name }}
              </option>
            </select>
          </div>

          <div class="form-group button-div">
            <ButtonSubmit
              @click="setNewPassword()"
              label="Set New Password"
              :disabled="!isFormValid || isSubmitting"
              class="login-submit-btn"
              :style="{
                border: '1px solid transparent',
                boxShadow: 'none',
                padding: '14px 20px',
                fontSize: '16px',
                fontWeight: '700',
                borderRadius: '4px',
                width: '100%',
                backgroundColor: brandData && brandData.main_color2 ? brandData.main_color2 : '#b2c225',
                color: brandData && brandData.button_text_color ? brandData.button_text_color : '#0E5071'
              }"
            />
          </div>
        </div>
      </section>
    </div>

    <div class="login-footer-wrap">
      <Footer :brand-data="brandData" />
    </div>
  </div>
</template>

<script>
export default {
  components: { ButtonSubmit, Footer },
  name: "ForgotPasswordSetPassword",
  props: {
    brandData: {
      type: Object,
      default: () => ({}),
    },
    logoPath: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    isLoggedIn: false,
    inputPassword: {
      password: "",
      confirmPassword: "",
    },
    showPasswordField: false,
    showConfirmPasswordField: false,
    isSubmitting: false,
    tempLoginPassword: '',
    userInfos: [],
    hours: 24,
    username: "",
    password: "",
    loginResults: [],
    Roles: "",
    currentRole: [],
    selectedLanguage: "en",
    availableLanguages: [
      { code: "af", name: "Afrikaans" },
      { code: "ak", name: "Akan" },
      { code: "sq", name: "Albanian" },
      { code: "am", name: "Amharic" },
      { code: "ar", name: "Arabic" },
      { code: "hy", name: "Armenian" },
      { code: "as", name: "Assamese" },
      { code: "ay", name: "Aymara" },
      { code: "az", name: "Azerbaijani" },
      { code: "bm", name: "Bambara" },
      { code: "ba", name: "Bashkir" },
      { code: "eu", name: "Basque" },
      { code: "be", name: "Belarusian" },
      { code: "bn", name: "Bengali" },
      { code: "bs", name: "Bosnian" },
      { code: "pt-br", name: "Brazilian Portuguese" },
      { code: "bg", name: "Bulgarian" },
      { code: "my", name: "Burmese" },
      { code: "yue", name: "Cantonese" },
      { code: "ca", name: "Catalan" },
      { code: "ceb", name: "Cebuano" },
      { code: "km", name: "Central Khmer" },
      { code: "ny", name: "Chichewa" },
      { code: "chk", name: "Chuukese" },
      { code: "co", name: "Corsican" },
      { code: "hr", name: "Croatian" },
      { code: "cs", name: "Czech" },
      { code: "da", name: "Danish" },
      { code: "dv", name: "Divehi" },
      { code: "nl", name: "Dutch" },
      { code: "en", name: "English" },
      { code: "eo", name: "Esperanto" },
      { code: "et", name: "Estonian" },
      { code: "ee", name: "Ewe" },
      { code: "fo", name: "Faroese" },
      { code: "fj", name: "Fijian" },
      { code: "fl", name: "Filipino" },
      { code: "fi", name: "Finnish" },
      { code: "fr", name: "French" },
      { code: "fr-ca", name: "French (Canada)" },
      { code: "gl", name: "Galician" },
      { code: "ka", name: "Georgian" },
      { code: "de", name: "German" },
      { code: "el", name: "Greek" },
      { code: "gn", name: "Guarani" },
      { code: "gu", name: "Gujarati" },
      { code: "ht", name: "Haitian Creole" },
      { code: "ha", name: "Hausa" },
      { code: "hw", name: "Hawaiian" },
      { code: "he", name: "Hebrew" },
      { code: "hi", name: "Hindi" },
      { code: "hmn", name: "Hmong" },
      { code: "hu", name: "Hungarian" },
      { code: "is", name: "Icelandic" },
      { code: "ig", name: "Igbo" },
      { code: "il", name: "Ilocano" },
      { code: "id", name: "Indonesian" },
      { code: "iu", name: "Inuktitut" },
      { code: "ga", name: "Irish" },
      { code: "it", name: "Italian" },
      { code: "ja", name: "Japanese" },
      { code: "jv", name: "Javanese" },
      { code: "kn", name: "Kannada" },
      { code: "kk", name: "Kazakh" },
      { code: "rw", name: "Kinyarwanda" },
      { code: "ko", name: "Korean" },
      { code: "ku", name: "Kurdish" },
      { code: "kb", name: "Kurdish (Sorani)" },
      { code: "ky", name: "Kyrgyz" },
      { code: "lo", name: "Lao" },
      { code: "la", name: "Latin" },
      { code: "lv", name: "Latvian" },
      { code: "ln", name: "Lingala" },
      { code: "lt", name: "Lithuanian" },
      { code: "lg", name: "Luganda" },
      { code: "lb", name: "Luxembourgish" },
      { code: "mk", name: "Macedonian" },
      { code: "mg", name: "Malagasy" },
      { code: "ms", name: "Malay" },
      { code: "ml", name: "Malayalam" },
      { code: "mt", name: "Maltese" },
      { code: "mr", name: "Marathi" },
      { code: "mh", name: "Marshallese" },
      { code: "mn", name: "Mongolian" },
      { code: "mi", name: "Māori" },
      { code: "ne", name: "Nepali" },
      { code: "no", name: "Norwegian" },
      { code: "nb", name: "Norwegian Bokmål" },
      { code: "oc", name: "Occitan" },
      { code: "or", name: "Odia" },
      { code: "om", name: "Oromo" },
      { code: "ps", name: "Pashto" },
      { code: "fa", name: "Persian" },
      { code: "pl", name: "Polish" },
      { code: "pt", name: "Portuguese" },
      { code: "pa", name: "Punjabi" },
      { code: "qu", name: "Quechua" },
      { code: "ro", name: "Romanian" },
      { code: "ru", name: "Russian" },
      { code: "sm", name: "Samoan" },
      { code: "gd", name: "Scottish Gaelic" },
      { code: "sr", name: "Serbian (Cyrillic)" },
      { code: "sr-latn", name: "Serbian (Latin)" },
      { code: "sn", name: "Shona" },
      { code: "zh", name: "Simplified Chinese" },
      { code: "sd", name: "Sindhi" },
      { code: "si", name: "Sinhalese" },
      { code: "sk", name: "Slovak" },
      { code: "sl", name: "Slovenian" },
      { code: "so", name: "Somali" },
      { code: "st", name: "Southern Sotho" },
      { code: "es", name: "Spanish" },
      { code: "su", name: "Sundanese" },
      { code: "sw", name: "Swahili" },
      { code: "sv", name: "Swedish" },
      { code: "tl", name: "Tagalog" },
      { code: "ty", name: "Tahitian" },
      { code: "tg", name: "Tajik" },
      { code: "ta", name: "Tamil" },
      { code: "tt", name: "Tatar" },
      { code: "te", name: "Telugu" },
      { code: "th", name: "Thai" },
      { code: "bo", name: "Tibetan" },
      { code: "ti", name: "Tigrinya" },
      { code: "to", name: "Tongan" },
      { code: "zh-tw", name: "Traditional Chinese" },
      { code: "ts", name: "Tsonga" },
      { code: "tr", name: "Turkish" },
      { code: "tk", name: "Turkmen" },
      { code: "uk", name: "Ukrainian" },
      { code: "ur", name: "Urdu" },
      { code: "ug", name: "Uyghur" },
      { code: "uz", name: "Uzbek" },
      { code: "vi", name: "Vietnamese" },
      { code: "cy", name: "Welsh" },
      { code: "fy", name: "Western Frisian" },
      { code: "xh", name: "Xhosa" },
      { code: "yi", name: "Yiddish" },
      { code: "yo", name: "Yoruba" },
      { code: "zu", name: "Zulu" }
    ],
    loginData: {},
    selectedLanguages: []
  }),
  computed: {

    filteredAvailableLanguages() {
      // Ensure selectedLanguages is an array
      const selectedLangsArray = Array.isArray(this.selectedLanguages)
        ? this.selectedLanguages
        : [this.selectedLanguages];

      const selectedValues = selectedLangsArray.map(lang =>
        typeof lang === 'object' ? lang.code : lang
      );

      return this.availableLanguages.filter(lang =>
        selectedValues.includes(lang.code)
      );
    },

    passwordRules() {
      return [
        v => !!v || 'Password is required',
        v => (v && v.length >= 8) || 'Password must be at least 8 characters long',
      ];
    },
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.inputPassword.password || 'Passwords do not match',
      ];
    },
    passwordStrength() {
      const password = this.inputPassword.password;
      if (!password) return { score: 0, text: '', color: '#4F4F4F' };

      let score = 0;

      // Length check
      if (password.length >= 8) score++;
      if (password.length >= 12) score++;

      // Complexity checks
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
      if (/\d/.test(password)) score++;
      if (/[^A-Za-z0-9]/.test(password)) score++;

      const strength = {
        0: { text: 'Very Weak', color: 'error' },
        1: { text: 'Weak', color: 'warning' },
        2: { text: 'Fair', color: 'orange' },
        3: { text: 'Good', color: 'success' },
        4: { text: 'Strong', color: 'success' },
        5: { text: 'Very Strong', color: 'success' }
      };

      return { score, ...strength[score] };
    },
    passwordRequirements() {
      const password = this.inputPassword.password;
      return [
        { text: 'At least 8 characters', met: password.length >= 8 },
        { text: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
        { text: 'Contains lowercase letter', met: /[a-z]/.test(password) },
        { text: 'Contains number', met: /\d/.test(password) },
        { text: 'Contains special character', met: /[^A-Za-z0-9]/.test(password) }
      ];
    },
    isFormValid() {
      return this.selectedLanguage &&
        this.inputPassword.password.length >= 8 &&
        this.inputPassword.password === this.inputPassword.confirmPassword;
    }
  },
  methods: {

    setCookie(name, value, hours) {
      const expires = new Date();
      expires.setTime(expires.getTime() + (hours * 60 * 60 * 1000));
      const encodedValue = encodeURIComponent(value);
      document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;secure;sameSite=Lax`;
    },

    async handleLogin() {
      // Set device cookie
      this.setCookie('a3b3dd4702b847b440c49c1198d77f85', 'f8beb37fb7fb9f0316f42142ac4382fbe94117b2', 24);
      
      try {
        const res = await api.post("auths", {
          email: this.username.toLowerCase().trim(),
          password: this.password,
        });

        if (res.status === 200) {
          // Store the entire response
          this.loginResults = res.data;
          this.setCookie('loginResults', JSON.stringify(this.loginResults), this.hours);
                      
          if(res.data) {
            if (this.loginResults.length <= 0) {
              flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Wrong username or password!");
              return null;
            } else if (this.loginResults[0].roles == "" || this.loginResults[0].roles == null) {
              flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Invalid user");
              return null;
            }
            
            // Store login data
            this.setCookie('passLoginUp', JSON.stringify(this.loginResults[0]), this.hours);
            this.setCookie('passBrandUp', JSON.stringify(this.loginResults[0].brandData), this.hours);
            this.setCookie('authenticate', JSON.stringify(this.isLoggedIn), this.hours);
            this.setCookie('mask_pii', this.loginResults[0].mask_pii || '0', this.hours);

            // Get announcements
            try {
              const UserAnnouncements = await api.post("/get-announcements-by-gospi-role", {
                org_id: this.loginResults[0].org_id,
                suborg_id: this.loginResults[0].suborgs,
                roles: this.loginResults[0].roles,
                ind_id: this.loginResults[0].ind_id,
                for_email: this.loginResults[0].email
              });
              if (UserAnnouncements.status === 200) {
                console.log('Get announcements success!', UserAnnouncements.data);
                this.$emit("passAnnouncementsUp", UserAnnouncements.data);
                this.setCookie('passAnnouncementsUp', JSON.stringify(UserAnnouncements.data));
              }
            } catch (error) {
              console.log(error);
            }

            // Save last login
            // Last login is now updated automatically during login process
            // No separate call needed
            // try {
            //   const lastLogin = await api.put("/auths/lastlogin", {
            //     email: this.username.toLowerCase().trim(),
            //   });
            //   if (lastLogin.status === 200) {
            //     console.log("Last login updated successfully");
            //   }
            // } catch (error) {
            //   console.log(error);
            // }

            // Give screens to logged in role
            this.Roles = this.loginResults[0].roles;
            try {
              const result = await api.get("roles");
              if (result.status === 200) {
                var rolesData = result.data;
                var newValue = this.Roles.split(", ");
                var newRoleName = newValue.map(
                  (r) => rolesData.find((f) => f.role_id == r).role_name
                );
                this.currentRole = newRoleName;
                
                this.$emit("passLoginUp", this.loginResults[0]);
                this.$emit("passBrandUp", this.loginResults[0].brandData);
                this.isLoggedIn = true;
                
                this.$router.push("/");
                this.$emit("authenticate", this.isLoggedIn);
                
                store.commit("addAuthString", this.decrypt(this.loginResults[0].auth_string, this.loginResults[0].seed));

                this.setCookie('passLoginUp', JSON.stringify(this.loginResults[0]), this.hours);
                this.setCookie('passBrandUp', JSON.stringify(this.loginResults[0].brandData), this.hours);
                this.setCookie('authenticate', JSON.stringify(this.isLoggedIn), this.hours);
                this.setCookie('mask_pii', this.loginResults[0].mask_pii || '0', this.hours);

                this.$flashMessage.show({
                  html: `<div style="background-color: ${
                    this.loginResults[0].brandData.accent_color1
                      ? this.loginResults[0].brandData.accent_color1
                      : "#F47820"
                  }; width:100%;">  
                    <p style="margin-bottom:0; margin-left:1rem; color: #fff;">Welcome ${this.loginResults[0].first_name}!</p>
                    <p style="margin-top:0; margin-left:1rem; color: #fff;">Role(s): ${this.currentRole.join(", ")}</p>
                  </div>`,
                  clickable: true,
                });
              }
            } catch (error) {
              console.log(error);
            }
          } else {
            flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Wrong username or password!");
            return null;
          }
        }
      } catch (error) {
        console.log('Login error:', error);
        flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Login failed after password reset!");
      }
    },

    encrypt(src, passphrase) {
      return CryptoJS.AES.encrypt(src, passphrase).toString();
    },
    decrypt(src, passphrase) {
      const bytes = CryptoJS.AES.decrypt(src, passphrase);
      return bytes.toString(CryptoJS.enc.Utf8);
    },
    getCookie(name) {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [cookieName, ...cookieParts] = cookie.split('=').map(c => c.trim());
        const cookieValue = cookieParts.join('=');
        if (cookieName === name) {
          try {
            const decodedValue = decodeURIComponent(cookieValue);
            if (decodedValue === null || decodedValue === undefined) {
              console.error('Cookie value is null or undefined');
              return null;
            }
            return decodedValue;
          } catch (e) {
            console.error('Error decoding cookie value:', e);
            return null;
          }
        }
      }
      return null;
    },
    checkLoginCookie() {
      try {
        const loginCookie = this.getCookie('loginResults');
        if (loginCookie) {
          this.loginResults = JSON.parse(loginCookie);
        } else {
          this.loginResults = [];
        }
      } catch (error) {
        console.log('Cookie parsing error:', error);
        this.setCookie('loginResults', '', -1);
        this.loginResults = [];
      }
    },
    userLog(status) {
      console.log(`User log: status=${status}`);
    },

    async handleFirstTimeLogin(passwordOverride = null) {

      const email = this.userInfos.email.toLowerCase().trim();
      const passwordToUse = passwordOverride || this.inputPassword.password;

      const logDebug = (label, data) => console.log(`${label}:`, data);
      const trySetCookie = (key, value, hours = this.hours) => {
        try {
          this.setCookie(key, JSON.stringify(value), hours);
        } catch (err) {
          console.warn(`Cookie storage error for ${key} (ignored):`, err);
        }
      };

      try {
        logDebug("First-time login attempt with", {
          email,
          password: passwordToUse,
          passwordLength: passwordToUse?.length ?? 0,
        });

        const res = await api.post("auths", { email, password: passwordToUse });

        logDebug("API Response", res);

        if (res.status !== 200 || !res.data?.length) {
          console.warn("Invalid login response");
          flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Login failed after password reset!");
          return;
        }

        const loginData = res.data[0];
        logDebug("Using login data", loginData);

        try {
          const decryptedAuthString = this.decrypt(loginData.auth_string, loginData.seed);
          this.setCookie("authString", decryptedAuthString, this.hours);
          store.commit("addAuthString", decryptedAuthString);
          logDebug("Stored decrypted authString", decryptedAuthString);
        } catch (err) {
          console.warn("AuthString decryption failed", err);
        }

        trySetCookie("loginResults", res.data);
        trySetCookie("passLoginUp", loginData);
        trySetCookie("passBrandUp", loginData.brandData);
        trySetCookie("authenticate", true);

        this.setCookie(
          "a3b3dd4702b847b440c49c1198d77f85",
          "f8beb37fb7fb9f0316f42142ac4382fbe94117b2",
          24
        );

        try {
          const announcements = await api.post("/get-announcements-by-gospi-role", {
            org_id: loginData.org_id,
            suborg_id: loginData.suborgs,
            roles: loginData.roles,
            ind_id: loginData.ind_id,
            for_email: loginData.email
          });
          if (announcements.status === 200) {
            logDebug("Announcements", announcements.data);
            this.$emit("passAnnouncementsUp", announcements.data);
            trySetCookie("passAnnouncementsUp", announcements.data);
          }
        } catch (error) {
          console.warn("Announcements error:", error);
        }

        try {
          const lastLogin = await api.put("/auths/lastlogin", { email });
          if (lastLogin.status === 200) {
            console.log("Last login updated");
          }
        } catch (error) {
          console.warn("Last login update error:", error);
        }

        this.Roles = loginData.roles;
        try {
          const roleRes = await api.get("roles");
          if (roleRes.status === 200) {
            const rolesData = roleRes.data;
            const roleIds = this.Roles.split(", ");
            this.currentRole = roleIds.map(r => rolesData.find(f => f.role_id == r)?.role_name || r);

            this.$emit("passLoginUp", loginData);
            this.$emit("passBrandUp", loginData.brandData);
            this.$emit("authenticate", true);

            this.isLoggedIn = true;
            this.userLog(1);

            this.$flashMessage.show({
              html: `<div style="background-color: ${loginData.brandData?.accent_color1 ?? "#F47820"}; width:100%;">
                        <p style="margin-bottom:0; margin-left:1rem; color: #fff;">Welcome ${loginData.first_name}!</p>
                        <p style="margin-top:0; margin-left:1rem; color: #fff;">Role(s): ${this.currentRole.join(", ")}</p>
                    </div>`,
              clickable: true,
            });

            logDebug("Navigation", "Redirecting to my profile...");
            this.$router.push("/my_profile");
          }
        } catch (error) {
          console.warn("Roles fetch error:", error);
        }

      } catch (error) {
        console.error("Login error:", {
          message: error.message,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data,
        });
        flashMessage(this.$flashMessage, "#F47820", "#ffffff", "Login failed after password reset!");
      }
    },

    setNewPassword() {
      if (this.isSubmitting) {
        return;
      }

      // Validate selected language
      if (!this.selectedLanguage) {
        flashMessage(this.$flashMessage, '#F47820', '#ffffff', "Please select a language!");
        return;
      }

      // Validate password length
      if (this.inputPassword.password.length < 8) {
        flashMessage(this.$flashMessage, '#F47820', '#ffffff', "Please Input 8+ characters!");
        return;
      }

      // Validate password match
      if (this.inputPassword.password !== this.inputPassword.confirmPassword) {
        flashMessage(this.$flashMessage, '#F47820', '#ffffff', "Passwords do not match!");
        return;
      }

      this.isSubmitting = true;

      // Check if using new token method or legacy ind_id method
      const token = this.$route.query.token;
      const hasLegacyIndId = this.$route.query.ind_id;

      if (token) {
        // NEW SECURE METHOD - Using token
        this.setPasswordWithToken(token);
      } else if (hasLegacyIndId) {
        // LEGACY METHOD - Using encrypted ind_id
        this.setPasswordLegacy();
      } else {
        this.isSubmitting = false;
        this.$flashMessage.show({ 
          type: "error", 
          title: "Invalid password reset link!", 
          message: "Please request a new password reset email." 
        });
        this.$router.push("/forgot-password");
      }
    },

    async setPasswordWithToken(token) {
      try {
        Swal.fire({
          title: '<span style="font-family:Open Sans;">Processing...</span>',
          html: '<span style="font-family:Open Sans;">Setting up your account</span>',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Set new password with token
        const response = await api.post("password-reset/set-password", {
          token: token,
          newPassword: this.inputPassword.password,
          language: this.selectedLanguage
        });

        if (response.data.success) {
          Swal.fire({
            title: "<span style='font-family:Open Sans;'>Password Successfully Set!</span>",
            html: `
              <div style="font-family:Open Sans;">
                <p>✅ Password updated</p>
                <p>✅ Language preference saved</p>
                <p>✅ Account ready</p>
                <br>
                <p>Redirecting to login...</p>
              </div>
            `,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
            allowOutsideClick: false
          }).then(() => {
            this.$router.push("/login");
          });
        } else {
          throw new Error(response.data.message || "Failed to set password");
        }
      } catch (e) {
        Swal.close();
        console.error("Password reset error:", e);
        this.isSubmitting = false;
        
        Swal.fire({
          title: '<span style="font-family:Open Sans;">Error</span>',
          html: `<span style="font-family:Open Sans;">${e.response?.data?.message || e.message || "Failed to set password"}</span>`,
          icon: "error",
          confirmButtonText: '<span style="font-family:Open Sans;">OK</span>',
          confirmButtonColor: "#d33"
        });
      }
    },

    async setPasswordLegacy() {
      // LEGACY METHOD - Keep existing implementation for backward compatibility
      const decrypedID = parseInt(this.decrypt(decodeURIComponent(this.$route.query.ind_id), "seed"));
      const encryptedPassword = {};

      // Validate URL param (Vue)
      if (this.$route.query.ind_id == "" || this.$route.query.ind_id == null) {
        this.isSubmitting = false;
        this.$flashMessage.show({ type: "error", title: "Invalid Action!", message: "" });
        return;
      }

      // Validate URL param (JS)
      if (decrypedID == null || isNaN(decrypedID)) {
        this.isSubmitting = false;
        this.$flashMessage.show({ type: "error", title: "Invalid Action!", message: "" });
        return;
      }

      // Fetch user info
      api.get("individuals/" + decrypedID).then((result) => {
        this.userInfos = result.data;

        encryptedPassword["password"] = this.encrypt(this.inputPassword.password, this.userInfos.seed);

        api.put("individuals-password/" + decrypedID, encryptedPassword).then(async () => {

          // Show processing message
          Swal.fire({
            title: '<span style="font-family:Open Sans;">Processing...</span>',
            html: '<span style="font-family:Open Sans;">Setting up your account</span>',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          // Save language preference
          await api.post("/save-language-preference", {
            ind_id: this.userInfos.ind_id,
            language: this.selectedLanguage
          }).then(async () => {
            console.log("Language preference saved!");

            // Add history log
            try {
              await api.post(`/create-user-history-log`, {
                ind_id: this.userInfos.ind_id,
                email: this.userInfos.email,
                screen: 'Set Password Screen',
                action: `Language Preference Changed`,
                action_description: `Changed language preference to: ${this.selectedLanguage}`,
              });
            } catch (err) {
              console.error('History log error:', err);
            }
          }).catch((e) => {
            console.error("Error saving language:", e.message);
          });

          // Store credentials for auto-login
          this.username = this.userInfos.email;
          this.password = this.inputPassword.password;

          Swal.fire({
            title: "<span style='font-family:Open Sans;'>Password Successfully Set!</span>",
            html: `
              <div style="font-family:Open Sans;">
                <p>✅ Password updated</p>
                <p>✅ Language preference saved</p>
                <p>✅ Account ready</p>
                <br>
                <p>You will now be automatically logged in.</p>
              </div>
            `,
            icon: "success",
            confirmButtonText: "<span style='font-family:Open Sans;'>Proceed to Login</span>",
            confirmButtonColor: this.loginData?.brandData?.accent_color1 ?? "#B2C225",
            allowOutsideClick: false
          }).then((result) => {
            if (result.isConfirmed) {
              // Call the exact same login method from LoginComponent
              this.handleLogin();
            }
          });

        }).catch((e) => {
          Swal.close();
          this.isSubmitting = false;
          
          Swal.fire({
            title: '<span style="font-family:Open Sans;">Error</span>',
            html: `<span style="font-family:Open Sans;">${e.message}</span>`,
            icon: "error",
            confirmButtonText: '<span style="font-family:Open Sans;">OK</span>',
            confirmButtonColor: "#d33"
          });
        });
      });
    }
  },

  async mounted() {
    // Check if we have a token (new secure method) or ind_id (legacy method)
    const hasToken = this.$route.query.token;
    
    if (!hasToken && !this.$route.query.ind_id) {
      this.$flashMessage.show({
        type: "error",
        title: "Invalid password reset link",
        message: "Please request a new password reset email."
      });
      this.$router.push("/forgot-password");
      return;
    }

    // If using new token method, verify the token
    let tokenIndId = null;
    if (hasToken) {
      try {
        const response = await api.post("password-reset/verify-token", {
          token: this.$route.query.token
        });
        
        if (!response.data.success) {
          this.$flashMessage.show({
            type: "error",
            title: "Invalid or expired reset link",
            message: "Please request a new password reset email."
          });
          this.$router.push("/forgot-password");
          return;
        }

        // ASSUMPTION: verify-token returns the individual's ind_id here.
        // Adjust this path if your API nests it differently, e.g. response.data.data.ind_id
        tokenIndId = response.data.ind_id ?? null;
      } catch (e) {
        console.error("Token verification error:", e);
        this.$flashMessage.show({
          type: "error",
          title: "Invalid reset link",
          message: "Please request a new password reset email."
        });
        this.$router.push("/forgot-password");
        return;
      }
    }

    // Legacy support for old ind_id method, plus token-based ind_id captured above
    let decrypiedID = tokenIndId;
    if (!decrypiedID && this.$route.query.ind_id) {
      try {
        decrypiedID = parseInt(this.decrypt(decodeURIComponent(this.$route.query.ind_id), "seed"));
        console.log('decrypiedID', decrypiedID);
      } catch (e) {
        console.error("Error decrypting ind_id:", e);
      }
    }
    // Save language preference
    try {
      // Check if decrypiedID is empty, null, or undefined
      if (!decrypiedID) {
        console.log("No valid ID provided. Skipping API call.");
        return;
      }

      const response = await api.post("/ind-data-for-language", {
        ind_id: decrypiedID
      });

      console.log("Language preference saved!");

      // Store the response data in loginData
      this.loginData = response.data;

    } catch (e) {
      console.error("Error saving language:", e.message);
    }

    if (this.loginData.suborgs) {
      try {
        // Check if loginData and suborgs exist
        if (!this.loginData?.suborgs) {
          console.log("No suborg data available. Using default languages.");
          this.selectedLanguages = ["en"];
          this.selectedLanguage = "en";
          return;
        }

        // Get the first suborg ID - handle array, string, or single value
        let suborgIds = [];

        // Normalize suborgs data to array format
        if (Array.isArray(this.loginData.suborgs)) {
          suborgIds = this.loginData.suborgs;
        } else if (typeof this.loginData.suborgs === 'string') {
          suborgIds = this.loginData.suborgs.split(',').map(id => id.trim()).filter(id => id);
        } else {
          suborgIds = [this.loginData.suborgs];
        }

        // Filter out any invalid/empty IDs
        suborgIds = suborgIds.filter(id => id && id !== '');

        // Check if suborgId is valid after extraction
        if (suborgIds.length === 0) {
          console.log("No valid suborg ID found. Using default languages.");
          this.selectedLanguages = ["en"];
          this.selectedLanguage = "en";
          return;
        }

        try {
          // Make API calls for all suborg IDs
          const apiPromises = suborgIds.map(async (suborgId) => {
            try {
              const res = await api.post("/get-selected-languages-suborg", {
                suborg_id: suborgId
              });
              return {
                suborgId,
                languages: res.data.languages || [],
                languageCount: (res.data.languages || []).length
              };
            } catch (err) {
              console.error(`Error fetching languages for suborg ${suborgId}:`, err.message);
              return {
                suborgId,
                languages: [],
                languageCount: 0
              };
            }
          });

          // Wait for all API calls to complete
          const results = await Promise.all(apiPromises);

          // Find the suborg with the most languages
          const bestSuborg = results.reduce((best, current) => {
            return current.languageCount > best.languageCount ? current : best;
          });

          if (bestSuborg.languageCount > 0) {
            this.selectedLanguages = bestSuborg.languages;
            console.log(`Selected suborg ${bestSuborg.suborgId} with ${bestSuborg.languageCount} languages`);
          } else {
            console.log("No suborg returned any languages. Using default languages.");
            this.selectedLanguages = ["en"];
          }

        } catch (err) {
          console.error("Error processing suborg language requests:", err.message);
          this.selectedLanguages = ["en"];
        }

        // Set the first available language as the selected one for the v-select
        if (this.selectedLanguages.length > 0) {
          this.selectedLanguage = this.selectedLanguages[0];
        }
      } catch (e) {
        console.error("Error fetching language preference:", e.message);
        this.selectedLanguages = ["en"];
        this.selectedLanguage = "en";
      }

    } else {
      // No suborg on this account at all — default to English-only, same as the suborg fallback above
      this.selectedLanguages = ["en"];
      this.selectedLanguage = "en";
    }
  },

};
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
}
.login-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
@media (min-width: 900px) {
  .login-main {
    flex-direction: row;
  }
}

.login-hero {
  display: none;
  position: relative;
  overflow: hidden;
  background: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"');
  color: #ffffff;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px;
  height: 100%;
  flex-shrink: 0;
}
@media (min-width: 900px) {
  .login-hero {
    display: flex;
    width: 50%;
  }
}

.login-hero-title {
  max-width: 420px;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ffffff;
}
.login-hero-tag {
  display: flex;
  align-items: center;
  gap: 16px;
}
.login-hero-tag-line {
  width: 48px;
  height: 1px;
  background: rgba(255, 255, 255, 0.4);
}
.login-hero-tag-text {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}
.login-hero-ticks {
  position: absolute;
  bottom: 48px;
  left: 48px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.tick {
  display: block;
  width: 4px;
  background: rgba(255, 255, 255, 0.4);
}
.tick-1 { height: 24px; }
.tick-2 { height: 48px; }
.tick-3 { height: 72px; }
.login-hero-graphic {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80%;
  height: 40%;
  background: rgba(255, 255, 255, 0.05);
  border-top-left-radius: 40px;
  overflow: hidden;
}
.stair {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  height: 16.6%;
}
.stair-1 { width: 50%; bottom: 0; }
.stair-2 { width: 42%; bottom: 16.6%; }
.stair-3 { width: 34%; bottom: 33.2%; }
.stair-4 { width: 26%; bottom: 49.8%; }
.stair-5 { width: 18%; bottom: 66.4%; }

.login-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 32px 24px;
  height: 100%;
  overflow-y: auto;
}
.login-panel-inner {
  width: 100%;
  max-width: 420px;
}
.login-heading {
  margin-bottom: 32px;
}
.login-panel-logo {
  display: block;
  max-height: 40px;
  width: auto;
  margin-bottom: 24px;
}
.login-heading h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #0b1c30;
  margin: 0 0 8px 0;
}
.login-subtext {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}
.language-intro {
  margin: 4px 0 16px 0;
}

.form-group {
  position: relative;
  margin-bottom: 20px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #434750;
  margin-bottom: 6px;
}
.form-control {
  height: 56px;
  font-size: 1rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  box-sizing: border-box;
  width: 100%;
  border-radius: 4px;
  padding: 16.5px 14px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.form-control::placeholder {
  color: #94a3b8;
  opacity: 1;
}
.form-control:focus {
  outline: none;
  border: 1.5px solid v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#002955"');
  box-shadow: none;
}
.language-select {
  cursor: pointer;
}

.password-input-wrap {
  position: relative;
}
.eye-toggle {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
}
.eye-icon-color {
  color: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"') !important;
}
.eye-toggle:hover {
  color: #f47820bd;
}

.strength-card {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 20px;
  background: #f8fafc;
}
.strength-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #434750;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.strength-icon {
  color: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"');
  font-size: 14px;
}
.strength-bar-track {
  height: 6px;
  border-radius: 3px;
  background: #e2e8f0;
  overflow: hidden;
  margin-bottom: 6px;
}
.strength-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}
.strength-error { background: #d32f2f; }
.strength-warning { background: #ed6c02; }
.strength-orange { background: #f57c00; }
.strength-success { background: #2e7d32; }

.strength-text {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 8px;
}
.strength-text-error { color: #d32f2f; }
.strength-text-warning { color: #ed6c02; }
.strength-text-orange { color: #f57c00; }
.strength-text-success { color: #2e7d32; }

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.requirements-list li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  padding: 2px 0;
}
.req-met {
  color: #2e7d32;
}
.req-unmet {
  color: #94a3b8;
}
.req-met i, .req-unmet i {
  font-size: 11px;
  width: 12px;
}

.button-div {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.button-div :deep(.btn-submit) {
  border-radius: 4px !important;
  font-weight: 700 !important;
  width: 100% !important;
}

.login-footer-wrap :deep(.footer-child) {
  padding: 24px 140px 24px 32px;
  margin: 0;
  height: auto;
  border-top: 1px solid #f1f5f9;
  align-items: center;
  font-size: 12px;
}
@media (max-width: 900px) {
  .login-footer-wrap :deep(.footer-child) {
    padding: 24px 32px;
    justify-content: center;
    text-align: center;
  }
}
.login-footer-wrap :deep(#footer-links) {
  align-items: center;
  gap: 4px;
}
.login-footer-wrap :deep(#footer-links a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
}
.login-footer-wrap :deep(#footer-links a:hover) {
  color: v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#123f75"');
}
.login-footer-wrap :deep(#powered-by a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 11px;
  color: #94a3b8;
  text-decoration: none;
}
</style>